import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { Delete, Edit, Print } from '@mui/icons-material';
import { deleteField } from 'firebase/firestore';
import {
  deleteGroup,
  deleteGroupWithReassignment,
  updateGroup,
  useActor,
} from '../../lib/audited-writes';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import { useUserContext } from '../../lib/users-context';
import { Group } from '../../lib/types';
import { FIRESTORE_BATCH_LIMIT } from '../../lib/constants';
import { formatGroupShort, sortGroups } from '../../lib/groups';
import { PRINT_TARGET_ID, groupsPrintStyles } from './manage-groups.utils';

const MAX_REASSIGN_ATTENDEES = FIRESTORE_BATCH_LIMIT - 1;

const DeleteGroupDialog = ({
  group,
  affectedCount,
  otherGroups,
  onClose,
  onDeleted,
  onError,
}: {
  group: Group | null;
  affectedCount: number;
  otherGroups: Group[];
  onClose: () => void;
  onDeleted: () => void;
  onError: (msg: string) => void;
}) => {
  const { attendees } = useFirebaseContext();
  const [targetRef, setTargetRef] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const requiresReassignment = affectedCount > 0;
  const noTargets = requiresReassignment && otherGroups.length === 0;
  const tooMany =
    requiresReassignment && affectedCount > MAX_REASSIGN_ATTENDEES;

  const handleClose = () => {
    if (isWorking) return;
    setTargetRef('');
    onClose();
  };

  const actor = useActor();

  const handleConfirm = async () => {
    if (!group) return;
    if (requiresReassignment && (noTargets || tooMany)) return;

    const liveAffectedRefs = attendees
      .filter((a) => a.groupRef === group.ref)
      .map((a) => a.ref);

    if (liveAffectedRefs.length > 0) {
      if (otherGroups.length === 0) {
        onError(
          'Attendees were just assigned to this group. Add another group first or unassign them manually.'
        );
        return;
      }
      if (liveAffectedRefs.length > MAX_REASSIGN_ATTENDEES) {
        onError(
          `Attendees were just assigned. Group now has ${liveAffectedRefs.length} attendees, exceeding the ${MAX_REASSIGN_ATTENDEES} limit for atomic reassignment.`
        );
        return;
      }
      if (!targetRef) return;
    }

    setIsWorking(true);
    try {
      if (liveAffectedRefs.length > 0) {
        await deleteGroupWithReassignment(
          actor,
          group,
          targetRef,
          liveAffectedRefs,
        );
      } else {
        await deleteGroup(actor, group);
      }
      setIsWorking(false);
      setTargetRef('');
      onDeleted();
      onClose();
    } catch (error) {
      console.error('Error deleting group', error);
      onError('Failed to delete group');
      setIsWorking(false);
    }
  };

  return (
    <Dialog
      open={Boolean(group)}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>Delete group</DialogTitle>
      <DialogContent>
        {group && (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Delete <strong>{formatGroupShort(group)}</strong>?
            </DialogContentText>
            {tooMany && (
              <Alert severity='error' sx={{ mb: 2 }}>
                This group has {affectedCount} attendees, which exceeds the
                limit of {MAX_REASSIGN_ATTENDEES} for a single atomic
                reassignment. Please reassign some attendees manually before
                deleting.
              </Alert>
            )}
            {noTargets && (
              <Alert severity='error' sx={{ mb: 2 }}>
                This is the only group, but {affectedCount} attendee(s) belong
                to it. Add another group first, or unassign the attendees
                manually.
              </Alert>
            )}
            {requiresReassignment && !noTargets && !tooMany && (
              <>
                <DialogContentText sx={{ mb: 2 }}>
                  {affectedCount} attendee(s) will be moved to another group.
                </DialogContentText>
                <FormControl fullWidth>
                  <InputLabel id='target-group-label'>
                    Move attendees to
                  </InputLabel>
                  <Select
                    labelId='target-group-label'
                    value={targetRef}
                    label='Move attendees to'
                    onChange={(e: SelectChangeEvent<string>) =>
                      setTargetRef(e.target.value)
                    }
                  >
                    {otherGroups.map((g) => (
                      <MenuItem key={g.ref} value={g.ref}>
                        {formatGroupShort(g)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            {!requiresReassignment && (
              <DialogContentText>
                No attendees are assigned to this group.
              </DialogContentText>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color='error'
          variant='contained'
          disabled={
            isWorking ||
            tooMany ||
            noTargets ||
            (requiresReassignment && !targetRef)
          }
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManageGroups = ({
  editingRotations,
  onEditGroup,
  onPrintGroup,
}: {
  editingRotations: boolean;
  onEditGroup: (group: Group) => void;
  onPrintGroup: (group: Group) => void;
}) => {
  const actor = useActor();
  const { groups, rotations, attendees } = useFirebaseContext();
  const { isAdmin } = useUserContext();
  const toast = useToast();
  const [deleting, setDeleting] = useState<Group | null>(null);
  const [rotationUpdatingRef, setRotationUpdatingRef] = useState<string | null>(
    null
  );
  type SortColumn = 'group' | 'rotation' | 'attendees';
  const [orderBy, setOrderBy] = useState<SortColumn>('rotation');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const attendeeCountByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    attendees.forEach((a) => {
      if (a.groupRef) counts[a.groupRef] = (counts[a.groupRef] || 0) + 1;
    });
    return counts;
  }, [attendees]);

  const rotationNameByRef = useMemo(() => {
    const map = new Map<string, string>();
    rotations.forEach((r) => map.set(r.ref, r.name));
    return map;
  }, [rotations]);

  const sortedGroups = useMemo(() => {
    const canonical = sortGroups(groups);
    if (orderBy === 'group') {
      return order === 'asc' ? canonical : [...canonical].reverse();
    }
    if (orderBy === 'rotation') {
      // Sort by rotation name (case-insensitive). Groups with no rotation
      // — or a stale `rotationRef` pointing at a deleted rotation — always
      // sink to the bottom regardless of direction, matching how the cell
      // renders them as "—" (a missing key, not the smallest value).
      // Within the same rotation, fall back to canonical group order so
      // the within-bucket ordering stays stable and predictable.
      return [...canonical].sort((a, b) => {
        const an = a.rotationRef
          ? rotationNameByRef.get(a.rotationRef)
          : undefined;
        const bn = b.rotationRef
          ? rotationNameByRef.get(b.rotationRef)
          : undefined;
        if (!an && !bn) return 0;
        if (!an) return 1;
        if (!bn) return -1;
        const diff = an.localeCompare(bn, undefined, { sensitivity: 'base' });
        return order === 'asc' ? diff : -diff;
      });
    }
    // Sort by attendee count; fall back to canonical group order on ties
    // so the within-count ordering stays stable and predictable.
    return [...canonical].sort((a, b) => {
      const ca = attendeeCountByGroup[a.ref] || 0;
      const cb = attendeeCountByGroup[b.ref] || 0;
      return order === 'asc' ? ca - cb : cb - ca;
    });
  }, [groups, orderBy, order, attendeeCountByGroup, rotationNameByRef]);

  const handleSort = (column: SortColumn) => {
    if (orderBy === column) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };

  const sortedRotations = useMemo(
    () =>
      [...rotations].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [rotations]
  );

  const validRotationRefs = useMemo(
    () => new Set(rotations.map((r) => r.ref)),
    [rotations]
  );

  const otherGroupsForDelete = useMemo(
    () =>
      deleting
        ? sortGroups(groups).filter((g) => g.ref !== deleting.ref)
        : [],
    [groups, deleting]
  );

  const handleRotationChange = async (
    group: Group,
    nextRef: string
  ): Promise<void> => {
    setRotationUpdatingRef(group.ref);
    try {
      await updateGroup(actor, group, {
        rotationRef: nextRef ? nextRef : deleteField(),
      });
      toast.success('Rotation updated');
    } catch (error) {
      console.error('Error updating rotation', error);
      toast.error('Failed to update rotation');
    } finally {
      setRotationUpdatingRef(null);
    }
  };

  return (
    <>
      {groupsPrintStyles}
      <Card id={PRINT_TARGET_ID}>
        <CardContent>
          <Typography
            variant='h5'
            data-print-show='true'
            sx={{ display: 'none', mb: 2 }}
          >
            Groups
          </Typography>
          {sortedGroups.length === 0 ? (
            <Typography color='text.secondary' textAlign='center'>
              No groups yet. Click <strong>Add group</strong> to create one.
            </Typography>
          ) : (
            <TableContainer>
              <Table
                size='small'
                sx={{
                  tableLayout: 'fixed',
                  '& tbody tr:last-child td': { borderBottom: 0 },
                }}
              >
                <colgroup>
                  <col style={{ width: 240 }} />
                  <col />
                  <col style={{ width: editingRotations ? 170 : 150 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 140 }} data-print-hide='true' />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sortDirection={orderBy === 'group' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'group'}
                        direction={orderBy === 'group' ? order : 'asc'}
                        onClick={() => handleSort('group')}
                      >
                        Group
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Teachers</TableCell>
                    <TableCell
                      sortDirection={orderBy === 'rotation' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'rotation'}
                        direction={orderBy === 'rotation' ? order : 'asc'}
                        onClick={() => handleSort('rotation')}
                      >
                        Rotation
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align='right'
                      sortDirection={orderBy === 'attendees' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'attendees'}
                        direction={orderBy === 'attendees' ? order : 'asc'}
                        onClick={() => handleSort('attendees')}
                      >
                        Attendees
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='right' data-print-hide='true'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedGroups.map((group) => {
                    const count = attendeeCountByGroup[group.ref] || 0;
                    // Defensively coerce stale refs (rotation deleted while
                    // the group still points at it) to '' so the Select
                    // shows "— None —" instead of an empty/uncontrolled state.
                    const currentRotation =
                      group.rotationRef && validRotationRefs.has(group.rotationRef)
                        ? group.rotationRef
                        : '';
                    const currentRotationName = currentRotation
                      ? rotationNameByRef.get(currentRotation) || ''
                      : '';
                    return (
                      <TableRow key={group.ref} hover>
                        <TableCell>{formatGroupShort(group)}</TableCell>
                        <TableCell>{group.teachers}</TableCell>
                        <TableCell>
                          {editingRotations ? (
                            <>
                              <Box data-print-hide='true'>
                                <FormControl
                                  size='small'
                                  fullWidth
                                  disabled={
                                    rotationUpdatingRef === group.ref
                                  }
                                >
                                  <InputLabel
                                    id={`rotation-${group.ref}-label`}
                                  >
                                    Rotation
                                  </InputLabel>
                                  <Select
                                    labelId={`rotation-${group.ref}-label`}
                                    value={currentRotation}
                                    label='Rotation'
                                    onChange={(
                                      e: SelectChangeEvent<string>
                                    ) =>
                                      handleRotationChange(
                                        group,
                                        e.target.value
                                      )
                                    }
                                  >
                                    <MenuItem value=''>
                                      <em>— None —</em>
                                    </MenuItem>
                                    {sortedRotations.map((r) => (
                                      <MenuItem key={r.ref} value={r.ref}>
                                        {r.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                              <Box
                                data-print-show='true'
                                sx={{ display: 'none' }}
                              >
                                {currentRotationName || '—'}
                              </Box>
                            </>
                          ) : (
                            currentRotationName || '—'
                          )}
                        </TableCell>
                        <TableCell align='right'>{count}</TableCell>
                        <TableCell align='right' data-print-hide='true'>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              gap: 0.5,
                            }}
                          >
                            <IconButton
                              size='small'
                              aria-label={`Print ${formatGroupShort(group)}`}
                              title='Print PDF'
                              onClick={() => onPrintGroup(group)}
                            >
                              <Print fontSize='small' />
                            </IconButton>
                            <IconButton
                              size='small'
                              aria-label={`Edit ${formatGroupShort(group)}`}
                              onClick={() => onEditGroup(group)}
                            >
                              <Edit fontSize='small' />
                            </IconButton>
                            {isAdmin && (
                              <IconButton
                                size='small'
                                aria-label={`Delete ${formatGroupShort(group)}`}
                                onClick={() => setDeleting(group)}
                              >
                                <Delete fontSize='small' />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <DeleteGroupDialog
        group={deleting}
        affectedCount={deleting ? attendeeCountByGroup[deleting.ref] || 0 : 0}
        otherGroups={otherGroupsForDelete}
        onClose={() => setDeleting(null)}
        onDeleted={() => toast.success('Group deleted')}
        onError={(message) => toast.error(message)}
      />
    </>
  );
};

export default ManageGroups;
